import React, {FC, Fragment, ReactElement, useMemo} from "react";
import {Grid, Typography} from "@mui/material";
import {observer} from "mobx-react";
import {ControlDivider} from "./NewEmployeeWizard";
import {NewEmployeeWizardState, TaxResidenceCountryType, WorkPermissionType} from "../../../store/NewEmployeeWizardState";
import {contractTypeLabel, salaryAmountLabel} from "./utils";
import {formatDate} from "../../../utils/DataFormat";
import CountrySelector from "../../../components/selector/CountrySelector";
import {Nil} from "../../../utils/Types";
import {Trans, useTranslation} from "react-i18next";
import {trans} from "../../../locale/utils";

interface IProps {
    state: NewEmployeeWizardState,
    printingMode?: boolean
}

const booleanValue = (b?: boolean) => {
    if (b) {
        return <Trans i18nKey="common.yes">Yes</Trans>
    } else {
        return <Trans i18nKey="common.no">No</Trans>
    }
}

const allowedToWorkBasedOnLabel = (allowedToWorkByCitizenship?: boolean, workPermissionType?: WorkPermissionType) => {
    if (allowedToWorkByCitizenship) {
        return trans("new_employee.personal_info.allowed_to_work_based_on_type.citizenship", "EU/EAA citizenship");
    } else {
        switch (workPermissionType) {
            case WorkPermissionType.SHORT_TERM_PERMISSION:
                return trans("new_employee.personal_info.allowed_to_work_based_on_type.short_term", "Short term permit")
            case WorkPermissionType.TEMP_RESIDENCE_PERMIT:
                return trans("new_employee.personal_info.allowed_to_work_based_on_type.temp_permit", "Temporary residence permit")
            case WorkPermissionType.PERM_RESIDENCE_PERMIT:
                return trans("new_employee.personal_info.allowed_to_work_based_on_type.perm_permit", "Permanent residence permit")
            case WorkPermissionType.DONT_KNOW:
                return trans("new_employee.personal_info.allowed_to_work_based_on_type.not_allowed", "Not allowed!")
        }
    }
}

const taxResidenceCountryLabel = (taxResidenceCountryType?: TaxResidenceCountryType, taxResidenceCountryValue?: string | null) => {
    switch (taxResidenceCountryType) {
        case TaxResidenceCountryType.ESTONIA:
            return "Estonia"
        case TaxResidenceCountryType.OTHER:
            return CountrySelector.findByCode(taxResidenceCountryValue!!)?.name
        case TaxResidenceCountryType.DONT_KNOW:
            return "Unknown"
    }
}

const documentReviewLine = (label: string, file: File | string | Nil) => {
    if (file) {
        return {q: label, a: (typeof file === "string") ? file : file.name}
    }
}

export const ReviewBlock: FC<IProps> = observer(({state, printingMode = false}): ReactElement => {
    const {t} = useTranslation()

    const personalDetailsBlock = [
        {q: <Trans i18nKey="new_employee.personal_info.lives_in_estonia">Does employee performs duties living in Estonia?</Trans>, a: booleanValue(state.performsWorkInEstonia)},
        {q: <Trans i18nKey="new_employee.personal_info.full_name">Employee full name</Trans>, a: `${state.name} ${state.surname}`},
        {q: <Trans i18nKey="new_employee.personal_info.personal_code">Personal code</Trans>, a: `${state.personalCode}`},
        {q: <Trans i18nKey="new_employee.personal_info.residential_address">Residential address</Trans>, a: `${state.residentialAddress}`},
        {
            q: <Trans i18nKey="new_employee.personal_info.allowed_to_work_based_on">Allowed to work based on</Trans>,
            a: allowedToWorkBasedOnLabel(state.allowedToWorkByCitizenship, state.workPermissionType)
        },
    ]

    const contractDetailsBlock = [
        {q: <Trans i18nKey="new_employee.contract_details.contract_type">Contract type</Trans>, a: contractTypeLabel(state.contractType!!)},
        {q: trans("new_employee.contract_details.position", "Position"), a: `${state.position}`},
        {q: trans("new_employee.contract_details.work_address", "Work address"), a: `${state.workAddress}`},
        {q: <Trans i18nKey="new_employee.contract_details.salary">Gross/brutto salary rate</Trans>, a: <>{state.rateValue}{" "}{salaryAmountLabel(state.rateType)}</>},
        {
            q: <Trans i18nKey="new_employee.contract_details.time_allocation">Time allocation</Trans>,
            a: state.worksFullTime ?
                    trans("new_employee.contract_details.full_time_allocation", "Full time") :
                    <>{trans("new_employee.contract_details.part_time_allocation", "Part time")}{" - "}{state.partTimeAllocationValue}</>
        },
        {q: trans("new_employee.review.start_date", "Contract start date"), a: formatDate(state.contractStartDate)},
        {
            q: trans("new_employee.review.end_date", "Contract end date"),
            a: state.contractHasEndDate ? formatDate(state.contractEndDate) : trans("new_employee.review.perm_contract", "Permanent contract without end date")
        },
        {
            q: trans("new_employee.review.probation_period", "Probation period"), a: state.probationPeriod ?
                    <><Trans
                            i18nKey={Number(state.probationPeriodMonths) === 1 ? "new_employee.review.probation_period_len_one" : "new_employee.review.probation_period_len_other"}
                            count={Number(state.probationPeriodMonths)}
                    >{state.probationPeriodMonths} month(s)</Trans></> :
                    trans("new_employee.review.no_probation_period", "No probation period")
        },
    ]

    const taxationDetailsBlock = [
        {
            q: <Trans i18nKey="new_employee.tax_and_templates.tax_residence">Tax residence country</Trans>,
            a: taxResidenceCountryLabel(state.taxResidenceCountryType, state.taxResidenceCountryValue)
        },
        state.taxResidenceCountryType === TaxResidenceCountryType.ESTONIA ?
                {q: t("new_employee.tax_and_templates.tax_deduction", "Does employee need tax deduction?"), a: booleanValue(state.taxDeductionNeeded)} : undefined,
        {
            q: trans("new_employee.review.should_we_register_worker", "Accountant should register new employee in the employee register"),
            a: booleanValue(state.shouldWeRegisterWorker)
        },
    ]

    const documentsBlock = [
        documentReviewLine(t("new_employee.document_upload.document", "Employee document (passport or ID card)"), state.document),
        documentReviewLine(t("new_employee.document_upload.work_permit", "Document that confirms the right to work (residence card or D-visa)"), state.workPermitDocument),
        documentReviewLine(t("new_employee.document_upload.tax_residence", "Tax residence statement"), state.taxResidenceStatementDocument),
        documentReviewLine(t("new_employee.document_upload.tax_deduction", "Signed tax deduction form"), state.taxDeductionFormDocument),
        documentReviewLine(t("new_employee.document_upload.work_contract", "Signed work contract"), state.workContractDocument),
    ]

    const blocks = [
        {label: <Trans i18nKey="new_employee.personal_info.header">Employee personal info</Trans>, details: personalDetailsBlock},
        {label: <Trans i18nKey="new_employee.contract_details.header">Contract details</Trans>, details: contractDetailsBlock},
        {label: <Trans i18nKey="new_employee.tax_and_templates.header">Taxation</Trans>, details: taxationDetailsBlock},
        {label: <Trans i18nKey="new_employee.document_upload.header">Documents upload</Trans>, details: documentsBlock},
    ]

    const firstItemLayout = useMemo(() => {
        if (printingMode) {
            return {
                xs: 5
            }
        } else {
            return {
                xs: 12, sm: 4
            }
        }
    }, [printingMode]);

    return <>
        {blocks.map((block, blockIndex) => <Fragment key={blockIndex}>
                    <br/>
                    <ControlDivider/>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant={"h6"}>{block.label}</Typography>
                        </Grid>
                        {block.details.map((it, index) =>
                                <Fragment key={index}>
                                    {it && <Grid item container xs={12} spacing={1} alignItems={"center"}>
                                        <Grid item {...firstItemLayout}>
                                            <Typography variant={"body2"}>{it.q}</Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant={"subtitle2"}>{it.a}</Typography>
                                        </Grid>
                                    </Grid>}
                                </Fragment>)
                        }
                    </Grid>
                </Fragment>
        )}
    </>
});

export default ReviewBlock;